import React, { useEffect, useState } from 'react';
import {
  TextField,
  Button,
  Autocomplete,
  Box,
  IconButton,
  Typography,
  Rating
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import axios from 'axios';

const NewCandidateForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    currentCompany: '',
    currentRole: '',
    currentLocation: '',
    compensation: '',
    rating: 0,
    skills: [],
    education: [
      { degree: '', institution: '', startDate: null, endDate: null, marks: '' }
    ],
    experience: [
      { organization: '', designation: '', department: '', description: '', startDate: null, endDate: null, skills: [] }
    ]
  });
  const [degrees, setDegrees] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [skills, setSkills] = useState([]);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetch('https://api.hireveda.com/degrees').then(res => res.json()).then(data => setDegrees(data));
    fetch('https://api.hireveda.com/institutions').then(res => res.json()).then(data => setInstitutions(data));
    fetch('https://api.hireveda.com/organizations').then(res => res.json()).then(data => setOrganizations(data));
    fetch('https://api.hireveda.com/designations').then(res => res.json()).then(data => setDesignations(data));
    fetch('https://api.hireveda.com/departments').then(res => res.json()).then(data => setDepartments(data));
    fetch('https://api.hireveda.com/skills').then(res => res.json()).then(data => setSkills(data));
  }, []);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleEducationChange = (idx, field, value) => {
    const updated = [...formData.education];
    updated[idx][field] = value;
    setFormData(prev => ({ ...prev, education: updated }));
  };

  const handleExperienceChange = (idx, field, value) => {
    const updated = [...formData.experience];
    updated[idx][field] = value;
    setFormData(prev => ({ ...prev, experience: updated }));
  };

  const addEducation = () => {
    setFormData(prev => ({
      ...prev,
      education: [...prev.education, { degree: '', institution: '', startDate: null, endDate: null, marks: '' }]
    }));
  };
  const removeEducation = (idx) => {
    setFormData(prev => ({
      ...prev,
      education: prev.education.filter((_, i) => i !== idx)
    }));
  };
  const addExperience = () => {
    setFormData(prev => ({
      ...prev,
      experience: [...prev.experience, { organization: '', designation: '', department: '', description: '', startDate: null, endDate: null, skills: [] }]
    }));
  };
  const removeExperience = (idx) => {
    setFormData(prev => ({
      ...prev,
      experience: prev.experience.filter((_, i) => i !== idx)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post('https://api.hireveda.com/candidates', formData);
      setMessage('Candidate added successfully!');
      setFormData({
        name: '', email: '', phone: '', currentCompany: '', currentRole: '', currentLocation: '', compensation: '', rating: 0, skills: [],
        education: [{ degree: '', institution: '', startDate: null, endDate: null, marks: '' }],
        experience: [{ organization: '', designation: '', department: '', description: '', startDate: null, endDate: null, skills: [] }]
      });
    } catch (error) {
      setMessage('Failed to add candidate');
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 700, mx: 'auto', p: 2 }}>
      <Typography variant="h5" mb={2}>New Candidate</Typography>
      {message && <Typography color={message.includes('success') ? 'green' : 'red'}>{message}</Typography>}
      <TextField label="Name" value={formData.name} onChange={e => handleChange('name', e.target.value)} fullWidth required margin="normal" />
      <TextField label="Email" value={formData.email} onChange={e => handleChange('email', e.target.value)} fullWidth required margin="normal" />
      <TextField label="Phone" value={formData.phone} onChange={e => handleChange('phone', e.target.value)} fullWidth required margin="normal" />
      <Autocomplete
        options={organizations.map(o => o.name)}
        value={formData.currentCompany}
        onChange={(_, v) => handleChange('currentCompany', v || '')}
        renderInput={params => <TextField {...params} label="Current Company" margin="normal" fullWidth />}
      />
      <Autocomplete
        options={designations.map(d => d.name)}
        value={formData.currentRole}
        onChange={(_, v) => handleChange('currentRole', v || '')}
        renderInput={params => <TextField {...params} label="Current Role" margin="normal" fullWidth />}
      />
      <TextField label="Current Location" value={formData.currentLocation} onChange={e => handleChange('currentLocation', e.target.value)} fullWidth margin="normal" />
      <TextField label="Compensation" value={formData.compensation} onChange={e => handleChange('compensation', e.target.value)} fullWidth required margin="normal" />
      <Box sx={{ my: 2 }}>
        <Typography component="legend">Rating</Typography>
        <Rating value={formData.rating} onChange={(_, v) => handleChange('rating', v || 0)} />
      </Box>
      <Autocomplete
        multiple
        options={skills.flatMap(cat => cat.subcategories.map(sub => sub.name))}
        value={formData.skills}
        onChange={(_, v) => handleChange('skills', v)}
        renderInput={params => <TextField {...params} label="Skills" margin="normal" fullWidth />}
      />
      <Box mt={3} mb={1}><Typography variant="h6">Education</Typography></Box>
      {formData.education.map((edu, idx) => (
        <Box key={idx} sx={{ border: '1px solid #eee', p: 2, mb: 2, borderRadius: 2 }}>
          <Autocomplete
            options={degrees.map(d => d.name)}
            value={edu.degree}
            onChange={(_, v) => handleEducationChange(idx, 'degree', v || '')}
            renderInput={params => <TextField {...params} label="Degree" margin="normal" fullWidth />}
          />
          <Autocomplete
            options={institutions.map(i => i.name)}
            value={edu.institution}
            onChange={(_, v) => handleEducationChange(idx, 'institution', v || '')}
            renderInput={params => <TextField {...params} label="Institution" margin="normal" fullWidth />}
          />
          <DatePicker
            label="Start Date"
            value={edu.startDate}
            onChange={v => handleEducationChange(idx, 'startDate', v)}
            renderInput={params => <TextField {...params} margin="normal" fullWidth />}
          />
          <DatePicker
            label="End Date"
            value={edu.endDate}
            onChange={v => handleEducationChange(idx, 'endDate', v)}
            renderInput={params => <TextField {...params} margin="normal" fullWidth />}
          />
          <TextField label="Marks" value={edu.marks} onChange={e => handleEducationChange(idx, 'marks', e.target.value)} fullWidth margin="normal" />
          <Box display="flex" justifyContent="flex-end">
            {formData.education.length > 1 && (
              <IconButton onClick={() => removeEducation(idx)} color="error"><RemoveCircleOutlineIcon /></IconButton>
            )}
            <IconButton onClick={addEducation} color="primary"><AddCircleOutlineIcon /></IconButton>
          </Box>
        </Box>
      ))}
      <Box mt={3} mb={1}><Typography variant="h6">Experience</Typography></Box>
      {formData.experience.map((exp, idx) => (
        <Box key={idx} sx={{ border: '1px solid #eee', p: 2, mb: 2, borderRadius: 2 }}>
          <Autocomplete
            options={organizations.map(o => o.name)}
            value={exp.organization}
            onChange={(_, v) => handleExperienceChange(idx, 'organization', v || '')}
            renderInput={params => <TextField {...params} label="Organization" margin="normal" fullWidth />}
          />
          <Autocomplete
            options={designations.map(d => d.name)}
            value={exp.designation}
            onChange={(_, v) => handleExperienceChange(idx, 'designation', v || '')}
            renderInput={params => <TextField {...params} label="Designation" margin="normal" fullWidth />}
          />
          <Autocomplete
            options={departments.map(d => d.name)}
            value={exp.department}
            onChange={(_, v) => handleExperienceChange(idx, 'department', v || '')}
            renderInput={params => <TextField {...params} label="Department" margin="normal" fullWidth />}
          />
          <TextField label="Description" value={exp.description} onChange={e => handleExperienceChange(idx, 'description', e.target.value)} fullWidth margin="normal" />
          <DatePicker
            label="Start Date"
            value={exp.startDate}
            onChange={v => handleExperienceChange(idx, 'startDate', v)}
            renderInput={params => <TextField {...params} margin="normal" fullWidth />}
          />
          <DatePicker
            label="End Date"
            value={exp.endDate}
            onChange={v => handleExperienceChange(idx, 'endDate', v)}
            renderInput={params => <TextField {...params} margin="normal" fullWidth />}
          />
          <Autocomplete
            multiple
            options={skills.flatMap(cat => cat.subcategories.map(sub => sub.name))}
            value={exp.skills}
            onChange={(_, v) => handleExperienceChange(idx, 'skills', v)}
            renderInput={params => <TextField {...params} label="Skills" margin="normal" fullWidth />}
          />
          <Box display="flex" justifyContent="flex-end">
            {formData.experience.length > 1 && (
              <IconButton onClick={() => removeExperience(idx)} color="error"><RemoveCircleOutlineIcon /></IconButton>
            )}
            <IconButton onClick={addExperience} color="primary"><AddCircleOutlineIcon /></IconButton>
          </Box>
        </Box>
      ))}
      <Button type="submit" variant="contained" color="primary" fullWidth sx={{ mt: 2 }}>Save</Button>
    </Box>
  );
};

export default NewCandidateForm;
